/* bm_peakExplorer_utils.js – Helfer & Parsing */
(function () {
  window.BM = window.BM || {};
  if (window.BM.__peakExplorerUtilsLoaded) return;
  window.BM.__peakExplorerUtilsLoaded = true;

  const FORMAT_LOCALE = 'de-DE';

  const clamp = (x, a, b) => Math.min(b, Math.max(a, Number(x)));
  const roundTo = (x, step) => Math.round(Number(x) / step) * step;
  const fmtDE = (n) => Number(n).toLocaleString(FORMAT_LOCALE);
  const toNum = (v, def) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
  };

  // Karten-Geometrie ↔ Maßstab
  const edgeKmFromScale = (printCm, denom) => (Number(printCm) * Number(denom)) / 100000;
  const scaleFromEdge = (edgeKm, printCm) => (Number(edgeKm) * 100000) / Number(printCm);

  // Dedupe-Helfer
  const nearlyEqual = (a, b, eps = 1e-9) => Math.abs(Number(a) - Number(b)) <= eps;
  const changed = (prev, next, eps = 1e-6) => !nearlyEqual(prev, next, eps);

  // Throttle per rAF (coalesced)
  function rafThrottle(fn) {
    let raf = 0;
    let lastArgs = null;

    const throttled = function (...args) {
      lastArgs = args;
      if (raf) return;

      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const currentArgs = lastArgs;
        lastArgs = null;
        fn.apply(this, currentArgs || []);
      });
    };

    throttled.cancel = () => {
      if (raf) {
        window.cancelAnimationFrame(raf);
        raf = 0;
      }
      lastArgs = null;
    };

    return throttled;
  }

  function prefersReducedMotion() {
    return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  }

  function formatDecimal(value, minimumFractionDigits = 0, maximumFractionDigits = 2) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';

    const rounded = Math.round(n * Math.pow(10, maximumFractionDigits)) / Math.pow(10, maximumFractionDigits);
    return rounded.toLocaleString(FORMAT_LOCALE, {
      minimumFractionDigits,
      maximumFractionDigits
    });
  }

  function formatCmValue(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    return formatDecimal(n, Number.isInteger(n) ? 0 : 1, 2);
  }

  function formatKmValue(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    return formatDecimal(n, 2, 2);
  }

  function createSizeInfo(valuesCm, meta = {}) {
    if (!Array.isArray(valuesCm)) return null;

    const cleanValues = valuesCm
      .map(Number)
      .filter(value => Number.isFinite(value) && value > 0);

    if (!cleanValues.length) return null;

    const shortestEdgeCm = Math.min(...cleanValues);
    const longestEdgeCm = Math.max(...cleanValues);
    const isMultiDimension = cleanValues.length > 1;
    const aspectRatio = shortestEdgeCm > 0 ? (longestEdgeCm / shortestEdgeCm) : 1;
    const displayLabel = isMultiDimension
      ? `${cleanValues.map(formatCmValue).join(' x ')} cm`
      : `${formatCmValue(shortestEdgeCm)} cm`;

    return {
      valuesCm: cleanValues.slice(),
      sourceWidthCm: cleanValues[0],
      sourceHeightCm: cleanValues[1] ?? cleanValues[0],
      printSizeCm: shortestEdgeCm,
      shortestEdgeCm,
      longestEdgeCm,
      landscapeWidthCm: longestEdgeCm,
      landscapeHeightCm: shortestEdgeCm,
      aspectRatio,
      isMultiDimension,
      displayLabel,
      unit: meta.unit || 'cm',
      source: meta.source || ''
    };
  }

  function sizeInfoFromCm(valueCm, meta = {}) {
    return createSizeInfo([valueCm], Object.assign({ unit: 'cm' }, meta));
  }

  function parseDimensionsFromString(input) {
    if (!input) return null;

    const str = String(input).toLowerCase().replace(/\s+/g, ' ').trim();
    const sanitized = str.replace(/1:\s*[\d.,]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (!sanitized) return null;

    const multiDimensionMatch = sanitized.match(/(\d+(?:[.,]\d+)?(?:\s*(?:x|×)\s*\d+(?:[.,]\d+)?)+)\s*(cm|mm)?/i);
    const numberSource = multiDimensionMatch ? multiDimensionMatch[1] : sanitized;

    const matches = [...numberSource.matchAll(/(\d+(?:[.,]\d+)?)/g)];
    if (!matches.length) return null;

    const rawValues = matches
      .map(match => Number(String(match[1]).replace(',', '.')))
      .filter(Number.isFinite);

    if (!rawValues.length) return null;

    const unit = (multiDimensionMatch?.[2] || (/\bmm\b/.test(sanitized) ? 'mm' : 'cm')).toLowerCase();
    const valuesCm = unit === 'mm'
      ? rawValues.map(value => value / 10)
      : rawValues;

    return createSizeInfo(valuesCm, {
      unit,
      source: 'string'
    });
  }

  function sizeInfoFromUnknown(input, meta = {}) {
    if (!input && input !== 0) return null;

    if (typeof input === 'object' && input.printSizeCm != null) {
      if (Array.isArray(input.valuesCm) && input.valuesCm.length) {
        return createSizeInfo(input.valuesCm, input);
      }
      return sizeInfoFromCm(input.printSizeCm, input);
    }

    if (typeof input === 'number') {
      return sizeInfoFromCm(input, meta);
    }

    if (typeof input === 'string') {
      return parseDimensionsFromString(input);
    }

    return null;
  }

  function sizeInfoFromDetail(detail) {
    if (!detail) return null;

    if (detail.sizeCM != null) return sizeInfoFromCm(detail.sizeCM, { source: 'detail:sizeCM' });
    if (detail.sizeCm != null) return sizeInfoFromCm(detail.sizeCm, { source: 'detail:sizeCm' });
    if (detail.print_size_cm != null) return sizeInfoFromCm(detail.print_size_cm, { source: 'detail:print_size_cm' });
    if (detail.sizeMM != null) return sizeInfoFromCm(Number(detail.sizeMM) / 10, { unit: 'mm', source: 'detail:sizeMM' });
    if (detail.size_mm != null) return sizeInfoFromCm(Number(detail.size_mm) / 10, { unit: 'mm', source: 'detail:size_mm' });

    const stringFields = ['print_size', 'size', 'format', 'dimension', 'dimensions'];
    for (const field of stringFields) {
      if (detail[field] == null) continue;
      const parsed = parseDimensionsFromString(detail[field]);
      if (parsed) {
        parsed.source = `detail:${field}`;
        return parsed;
      }
    }

    return null;
  }

  function sizeInfoFromVariant(variant) {
    if (!variant) return null;

    const metafields = variant.metafields || variant?.object?.metafields || {};

    if (metafields.print_size_cm != null) {
      return sizeInfoFromCm(metafields.print_size_cm, { source: 'metafield:print_size_cm' });
    }

    const metafieldStringKeys = ['print_size', 'size', 'format'];
    for (const key of metafieldStringKeys) {
      if (metafields[key] == null) continue;
      const parsed = parseDimensionsFromString(metafields[key]);
      if (parsed) {
        parsed.source = `metafield:${key}`;
        return parsed;
      }
    }

    const names = variant.option_names || variant.options?.map?.((_, i) => variant[`option${i + 1}_name`]);
    const values = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);

    if (names && values) {
      for (let i = 0; i < names.length; i += 1) {
        const name = String(names[i] || '').toLowerCase();
        if (!/(größe|size|format)/.test(name)) continue;

        const parsed = parseDimensionsFromString(values[i]);
        if (parsed) {
          parsed.source = `option:${name}`;
          return parsed;
        }
      }
    }

    const titleParsed = parseDimensionsFromString(variant.title || '');
    if (titleParsed) {
      titleParsed.source = 'title';
      return titleParsed;
    }

    return null;
  }

  function parseCmFromString(input) {
    return parseDimensionsFromString(input)?.printSizeCm ?? null;
  }

  function cmFromDetail(detail) {
    return sizeInfoFromDetail(detail)?.printSizeCm ?? null;
  }

  function cmFromVariant(variant) {
    return sizeInfoFromVariant(variant)?.printSizeCm ?? null;
  }

  window.BM.util = {
    clamp,
    roundTo,
    fmtDE,
    toNum,
    nearlyEqual,
    changed,
    rafThrottle,
    prefersReducedMotion,
    edgeKmFromScale,
    scaleFromEdge,
    formatDecimal,
    formatCmValue,
    formatKmValue,
    createSizeInfo,
    sizeInfoFromCm,
    sizeInfoFromUnknown,
    parseDimensionsFromString,
    parseCmFromString,
    sizeInfoFromDetail,
    sizeInfoFromVariant,
    cmFromDetail,
    cmFromVariant
  };
})();
